import { useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { IoIosAddCircleOutline } from "react-icons/io";
import Loader from "../../../../reusableUI/loader";
import {
  formatPhoneNumber,
  capitalizeFirstLetter,
} from "../../../../utils/Functions";
import {
  CommonImages,
  partnerPortalImages,
} from "../../../../reusableUI/images";
import { FiDownload, FiEdit, FiMail } from "react-icons/fi";
import { RiUserAddFill } from "react-icons/ri";
import { MdClose, MdDeleteOutline } from "react-icons/md";
import {
  MdOutlineFilterList,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from "react-icons/md";
import Papa from "papaparse";
import ToggleTabs from "../../../../reusableUI/tap";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import { showToast } from "../../../../reusableUI/toaster";
import { getLookupData } from "../../../../Service/lookup";
import secureLocalStorage from "react-secure-storage";
import moment from "moment";
import { Upload, Button, message, Space, Card } from "antd";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import {
  SpecialistAdd,
  editSpecialist,
  DeleteSpecialist,
  getSpecialistPartner,
} from "../../../../Service/partnerAPI";
import {
  getPartnerInfo,
  resendWelcomEmailForSpecialist,
} from "../../../../Service/RM";

const { Dragger } = Upload;

/** -----------------------------
 * BULK UPLOAD
 * ----------------------------- */
const BULK_REQUIRED_HEADERS = [
  "role",
  "title",
  "fullname",
  "dob",
  "phone",
  "email",
];
const BULK_ALLOWED_ROLES = ["physiotherapist", "nurse", "nursing-assistant"];

const normalizeHeaderKey = (k) =>
  String(k ?? "")
    .trim()
    .toLowerCase();

const normalizeRoleToken = (raw) =>
  String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ");

const deriveRoleAndSubRole = (roleRaw) => {
  const t = normalizeRoleToken(roleRaw);

  if (t === "physiotherapist")
    return { role: "physiotherapist", subRole: "physiotherapist" };
  if (t === "nurse") return { role: "nurse", subRole: "nurse" };
  if (t === "nursing assistant" || t === "nursingassistant")
    return { role: "nurse", subRole: "nursing-assistant" };

  return null;
};

const cleanPhone10Digits = (rawPhone) => {
  let digits = String(rawPhone ?? "").replace(/\D/g, "");
  if (digits.length > 10) digits = digits.slice(-10);
  if (digits.length !== 10) return null;
  return digits;
};

const normalizeName = (v) =>
  String(v ?? "")
    .replace(/\s+/g, " ")
    .trim();

const isValidName = (v) => {
  const n = normalizeName(v);
  if (!n) return false;
  if (n.length < 3 || n.length > 50) return false;
  if (!/^[A-Za-z ]+$/.test(n)) return false;
  const lettersCount = (n.match(/[A-Za-z]/g) || []).length;
  return lettersCount >= 3;
};

const sanitizeNameInput = (v) => {
  const s = String(v ?? "").replace(/[^A-Za-z\s]/g, "");
  return s.replace(/\s+/g, " ").trimStart();
};

const getDobBounds = () => {
  const todayMoment = moment().startOf("day");
  return {
    minDobMoment: todayMoment.clone().subtract(100, "years"),
    maxDobMoment: todayMoment,
  };
};

const isDobWithinLast100Years = (dateLike) => {
  const m = moment(dateLike);
  if (!m.isValid()) return false;
  const { minDobMoment, maxDobMoment } = getDobBounds();
  return !m.isBefore(minDobMoment, "day") && !m.isAfter(maxDobMoment, "day");
};

const isValidEmail = (val) => {
  const v = String(val ?? "").trim();
  if (!v) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(v);
};

const normalizeEmail = (val) =>
  String(val ?? "")
    .trim()
    .toLowerCase();

const normalizePhoneTo10 = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length > 10 ? digits.slice(-10) : digits;
};

const getExistingContacts = (list) => {
  const phoneSet10 = new Set();
  const emailSet = new Set();

  (list || []).forEach((item) => {
    const phone10 = normalizePhoneTo10(item?.phone);
    if (phone10 && phone10.length === 10) phoneSet10.add(phone10);

    const em = normalizeEmail(item?.personalInfo?.basicInfo?.otherInfo?.email);
    if (em) emailSet.add(em);
  });

  return { phoneSet10, emailSet };
};

const excelSerialToDate = (serial) => {
  const n = Number(serial);
  if (!Number.isFinite(n)) return null;

  const dc = XLSX?.SSF?.parse_date_code?.(n);
  if (dc && dc.y && dc.m && dc.d) return new Date(dc.y, dc.m - 1, dc.d);

  const utcDays = Math.floor(n - 25569);
  const utcValue = utcDays * 86400;
  const dateInfo = new Date(utcValue * 1000);
  return new Date(
    dateInfo.getUTCFullYear(),
    dateInfo.getUTCMonth(),
    dateInfo.getUTCDate(),
  );
};

const parseDobBulkCell = (dobVal) => {
  if (dobVal === null || dobVal === undefined || dobVal === "") return null;

  if (dobVal instanceof Date && !isNaN(dobVal)) {
    return moment(dobVal).startOf("day").toDate();
  }

  const s = String(dobVal ?? "").trim();
  if (!s) return null;

  if (/^\d+(\.\d+)?$/.test(s)) {
    const dt = excelSerialToDate(Number(s));
    if (dt && moment(dt).isValid()) return moment(dt).startOf("day").toDate();
  }

  const parsed = moment(
    s,
    ["DD/MM/YYYY", "D/M/YYYY", "DD-MM-YYYY", "D-M-YYYY", "YYYY-MM-DD"],
    true,
  );
  if (!parsed.isValid()) return null;
  return parsed.startOf("day").toDate();
};

/** -----------------------------
 * FILTER HELPERS
 * ----------------------------- */
const ROLE_FILTERS = [
  { key: "nurse", label: "Nurse" },
  { key: "physiotherapist", label: "Physiotherapist" },
  { key: "nursing-assistant", label: "Nursing Assistant" },
];

const PROFILE_FILTERS = [
  { key: 0, label: "0/2" },
  { key: 1, label: "1/2" },
  { key: 2, label: "2/2" },
];

const getNormalizedBackendSubRole = (u) => {
  const t = normalizeRoleToken(u?.subRole || u?.role || "");
  if (t === "physiotherapist") return "physiotherapist";
  if (t === "nursing assistant" || t === "nursingassistant")
    return "nursing-assistant";
  return "nurse";
};

const getProfileCompletionScore = (original) => {
  let count = 0;
  if (original?.personalInfo?.completed) count += 1;
  const emergencyCount = Array.isArray(original?.emergencyContactInfo)
    ? original.emergencyContactInfo.length
    : 0;
  if (emergencyCount === 2) count += 1;
  return count; // 0,1,2
};

const MCT = ({
  searchTerm,
  onUploadSuccess,
  careTeamData,
  partnerID: partnerIDProp,
  partnerName,
  show,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const partnerIDFromState =
    location?.state?.partnerID ||
    location?.state?.partnerId ||
    location?.state?.userID ||
    undefined;

  const partnerID = partnerIDProp ?? partnerIDFromState;

  const isInjectedCareTeam = careTeamData !== undefined;
  const isReadOnly = isInjectedCareTeam || Boolean(partnerID);
  const canManage = !isReadOnly;

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [title, setTitle] = useState("");
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [code, setCode] = useState("91");
  const [phone, setPhone] = useState("");
  const [fee, setFee] = useState("");
  const [email, setEmail] = useState("");

  const [paginationPage, setPaginationPage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [userList, setUserList] = useState([]);
  const [noUsersFound, setNoUsersFound] = useState(false);
  const [casedata, setCase] = useState([]);
  const [maxDate, setMaxDate] = useState("");
  const [minDate, setMinDate] = useState("");
  const [currentItem, setCurrentItem] = useState(null);
  const [edit, setEdit] = useState(false);
  const [entryMode, setEntryMode] = useState("single");

  /** Page count dropdown (persist) */
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const savedPageCount = localStorage.getItem("pageCount");
    return savedPageCount ? parseInt(savedPageCount, 10) : 50;
  });

  const [selectedTab] = useState("Specialist");
  const [serviceInfoPage] = useState(false);

  /** Default sort: createdAt latest */
  const [sortBy, setSortBy] = useState(() => {
    const savedSortBy = localStorage.getItem("sortBy");
    if (savedSortBy) return JSON.parse(savedSortBy);
    return [{ id: "createdAt", desc: true }];
  });

  const [csvData, setCsvData] = useState([]);
  const [fileList, setFileList] = useState([]);

  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkValidUsers, setBulkValidUsers] = useState([]);
  const [resendPreviewModal, setResendPreviewModal] = useState({
    show: false,
    specialistID: "",
    hospital: "",
    specialist: "",
    role: "",
    email: "",
  });
  const [isResendSending, setIsResendSending] = useState(false);

  /** ✅ Filters UI like screenshot */
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [roleFilterKeys, setRoleFilterKeys] = useState([]); // "nurse" | "physiotherapist" | "nursing-assistant"
  const [profileFilterKeys, setProfileFilterKeys] = useState([]); // 0|1|2
  const [onboardSort, setOnboardSort] = useState("latest"); // radio: latest|oldest
  const filterRef = useRef(null);

  const existingContacts = useMemo(
    () => getExistingContacts(userList),
    [userList],
  );

  const getStoredRoleNormalized = () =>
    (secureLocalStorage.getItem("role") ?? "").toString().toLowerCase().trim();

  const providerType = getStoredRoleNormalized();

  useEffect(() => {
    const { minDobMoment, maxDobMoment } = getDobBounds();
    setMaxDate(maxDobMoment.format("YYYY-MM-DD"));
    setMinDate(minDobMoment.format("YYYY-MM-DD"));
  }, []);

  /** close filter on outside click */
  useEffect(() => {
    const onDocClick = (e) => {
      if (!isFilterOpen) return;
      if (filterRef.current && !filterRef.current.contains(e.target))
        setIsFilterOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isFilterOpen]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      try {
        if (careTeamData !== undefined) {
          if (!cancelled)
            setUserList(Array.isArray(careTeamData) ? careTeamData : []);
          return;
        }

        const lookupRes = await getLookupData();
        if (!cancelled && lookupRes?.error === false)
          setCase(lookupRes.data || []);

        await getUserList(cancelled);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [partnerID, careTeamData]);

  const caseList = Array.isArray(casedata)
    ? casedata.filter((item) => item.type === "title")
    : [];
  const caseCode = Array.isArray(casedata)
    ? casedata.filter((item) => item.type === "phonecode")
    : [];

  const handleDobChange = (e) => setDob(e.target.value);

  const getUserList = async (cancelledFlag = false) => {
    try {
      const storageRole = getStoredRoleNormalized();
      let res;

      if (storageRole === "hospital") {
        res = await getSpecialistPartner();
        const hasError = res?.error === true || res?.error === "true";
        const list = Array.isArray(res?.data)
          ? res.data.filter((item) => item.role !== "patient")
          : [];
        if (!hasError) {
          if (!cancelledFlag) setUserList(list);
        } else {
          if (!cancelledFlag) setUserList([]);
        }
        return;
      }

      if (!partnerID) {
        if (!cancelledFlag) setUserList([]);
        return;
      }

      res = await getPartnerInfo(partnerID);

      const hasError = res?.error === true || res?.error === "true";
      const list = Array.isArray(res?.data?.specialists)
        ? res.data.specialists
        : Array.isArray(res?.data)
          ? res.data
          : [];

      if (!hasError) {
        if (!cancelledFlag) setUserList(list);
      } else {
        if (!cancelledFlag) setUserList([]);
      }
    } catch (error) {
      if (!cancelledFlag) setUserList([]);
    }
  };

  const AddUser = () => {
    if (!canManage) return;

    setEdit(false);
    setRole("");
    setTitle("");
    setName("");
    setPhone("");
    setDob("");
    setEmail("");
    setFee("");
    setEntryMode("single");
    setCsvData([]);
    setFileList([]);
    setBulkErrors([]);
    setBulkValidUsers([]);
    setIsOpen(true);
  };

  const downloadSampleExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("HCP Template");

      ws.columns = [
        { header: "role", key: "role", width: 22 },
        { header: "title", key: "title", width: 10 },
        { header: "fullname", key: "fullname", width: 28 },
        { header: "dob", key: "dob", width: 14 },
        { header: "phone", key: "phone", width: 14 },
        { header: "email", key: "email", width: 28 },
      ];

      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = { vertical: "middle", horizontal: "center" };

      ws.addRow({
        role: "nurse",
        title: "Mr",
        fullname: "John Doe",
        dob: "15/06/1990",
        phone: "9876543210",
        email: "john.doe@example.com",
      });

      ws.dataValidations.add("A2:A500", {
        type: "list",
        allowBlank: false,
        formulae: [`"${BULK_ALLOWED_ROLES.join(",")}"`],
        showErrorMessage: true,
        errorStyle: "error",
        errorTitle: "Invalid role",
        error: `Role must be one of: ${BULK_ALLOWED_ROLES.join(", ")}`,
      });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "HCP_Sample_Template.xlsx");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success("Sample template downloaded successfully!");
    } catch (e) {
      message.error("Failed to download sample template");
    }
  };

  const resetForm = () => {
    setRole("");
    setTitle("");
    setName("");
    setPhone("");
    setDob("");
    setEmail("");
    setCode("91");
    setFee("");
    setIsOpen(false);
    setCurrentItem(null);
    setEdit(false);

    setEntryMode("single");
    setCsvData([]);
    setFileList([]);
    setBulkErrors([]);
    setBulkValidUsers([]);
  };

  const validateUniqueSingle = () => {
    const enteredPhone10 = normalizePhoneTo10(code + phone);
    const enteredEmail = normalizeEmail(email);

    const currentPhone10 = edit ? normalizePhoneTo10(currentItem?.phone) : "";
    const currentEmail = edit
      ? normalizeEmail(currentItem?.personalInfo?.basicInfo?.otherInfo?.email)
      : "";

    if (
      enteredPhone10 &&
      enteredPhone10.length === 10 &&
      existingContacts.phoneSet10.has(enteredPhone10) &&
      enteredPhone10 !== currentPhone10
    ) {
      showToast("Phone number already exists.", "Error");
      return false;
    }

    if (
      enteredEmail &&
      existingContacts.emailSet.has(enteredEmail) &&
      enteredEmail !== currentEmail
    ) {
      showToast("Email already exists.", "Error");
      return false;
    }

    return true;
  };

  const validateOnboardOrEdit = () => {
    if (!title.trim()) return (showToast("Title is required.", "Error"), false);

    const nm = normalizeName(name);
    if (!nm) return (showToast("Name is required.", "Error"), false);
    if (!isValidName(nm))
      return (
        showToast(
          "Name must be 3-50 chars, letters/spaces only, and contain at least 3 letters.",
          "Error",
        ),
        false
      );

    const dobValue = String(dob || "").trim();
    if (!dobValue)
      return (showToast("Date of birth is required.", "Error"), false);
    const parsedDob = moment(dobValue, "YYYY-MM-DD", true);
    if (!parsedDob.isValid() || !isDobWithinLast100Years(parsedDob))
      return (showToast("Invalid DOB", "Error"), false);

    if (!role.trim()) return (showToast("Role is required.", "Error"), false);

    if (!code.trim())
      return (showToast("Phone code is required.", "Error"), false);

    if (!phone.trim())
      return (showToast("Contact Number is required.", "Error"), false);
    if (String(phone).length !== 10)
      return (
        showToast("Contact Number must be exactly 10 digits.", "Error"),
        false
      );

    if (!email.trim()) return (showToast("Email is required.", "Error"), false);
    if (!isValidEmail(email))
      return (showToast("Please enter a valid email address.", "Error"), false);

    if (!validateUniqueSingle()) return false;

    return true;
  };

  const SaveUser = async () => {
    if (!canManage) return;
    if (!validateOnboardOrEdit()) return;

    const payload = {
      type: "single",
      onboardedBy: { partnerType: providerType?.toLowerCase() || "" },
      phone: Number((code + phone).replace(/\s+/g, "")),
      role: role === "Nursing-Assistant" ? "nurse" : role.toLowerCase(),
      subRole:
        role === "Nurse"
          ? "nurse"
          : role === "Physiotherapist"
            ? "physiotherapist"
            : "nursing-assistant",
      personalInfo: {
        basicInfo: {
          nameInfo: { title, fullName: normalizeName(name) },
          otherInfo: {
            gender: undefined,
            phone: Number((code + phone).replace(/\s+/g, "")),
            email: email.trim(),
            dob: new Date(dob),
          },
        },
      },
    };

    try {
      setLoading(true);
      const res = await SpecialistAdd(payload);

      if (res?.error === false || res?.error === "false") {
        showToast("User Added Successfully", "Success");
        resetForm();
        await getUserList(false);
      } else {
        showToast(res?.message || "Failed to add user", "Error");
      }
    } catch (err) {
      showToast("Something went wrong while adding the user", "Error");
    } finally {
      setLoading(false);
    }
  };

  const mapBackendToUIRole = (item) => {
    const sub = (item?.subRole ?? "").toString().toLowerCase();
    if (sub === "physiotherapist") return "Physiotherapist";
    if (sub === "nursing-assistant") return "Nursing-Assistant";
    return "Nurse";
  };

  const OpenEdit = (item) => {
    if (!canManage) return;

    setIsOpen(true);
    setEdit(true);
    setCurrentItem(item);

    const titleVal = item?.personalInfo?.basicInfo?.nameInfo?.title || "";
    const fullName = item?.personalInfo?.basicInfo?.nameInfo?.fullName || "";
    const phoneStr = String(item?.phone || "").startsWith("91")
      ? String(item?.phone).slice(2)
      : String(item?.phone || "");
    const dobVal = item?.personalInfo?.basicInfo?.otherInfo?.dob
      ? moment(item?.personalInfo?.basicInfo?.otherInfo?.dob).format(
          "YYYY-MM-DD",
        )
      : "";
    const mail = item?.personalInfo?.basicInfo?.otherInfo?.email || "";

    setRole(mapBackendToUIRole(item));
    setTitle(titleVal);
    setCode("91");
    setName(fullName);
    setPhone(phoneStr);
    setDob(dobVal);
    setEmail(mail);

    setEntryMode("single");
    setCsvData([]);
    setFileList([]);
    setBulkErrors([]);
    setBulkValidUsers([]);
  };

  const SaveEdit = async () => {
    if (!canManage) return;
    if (!currentItem?._id) return;

    if (!validateOnboardOrEdit()) return;

    const normalizedRole =
      role === "Nursing-Assistant" ? "nurse" : role.toLowerCase();
    const normalizedSubRole =
      role === "Nurse"
        ? "nurse"
        : role === "Physiotherapist"
          ? "physiotherapist"
          : "nursing-assistant";

    const payload = {
      id: currentItem._id,
      title,
      fullName: normalizeName(name),
      phone: Number((code + phone).replace(/\s+/g, "")),
      dob: new Date(dob),
      role: normalizedRole,
      subRole: normalizedSubRole,
      email: email.trim(),
    };

    try {
      setLoading(true);
      const res = await editSpecialist(payload);

      if (res?.error === false || res?.error === "false") {
        showToast("Updated Details Successfully", "Success");
        resetForm();
        await getUserList(false);
      } else {
        showToast(res?.message || "Failed to update details", "Error");
      }
    } catch (error) {
      showToast("Something went wrong while updating", "Error");
    } finally {
      setLoading(false);
    }
  };

  const DeleteUser = async (id) => {
    window.alert("Please draft a mail to support@rizzsoftware.com requesting deletion.");
    // if (!canManage) return;

    // const confirmed = window.confirm(
    //   "Are you sure you want to delete this user?",
    // );
    // if (!confirmed) return;

    // try {
    //   setLoading(true);
    //   const res = await DeleteSpecialist(id);
    //   if (res?.error === false || res?.error === "false") {
    //     await getUserList(false);
    //     showToast("User Deleted Successfully", "Success");
    //   } else {
    //     showToast(res?.message || "Failed to delete user", "Error");
    //   }
    // } catch (err) {
    //   showToast("Something went wrong", "Error");
    // } finally {
    //   setLoading(false);
    // }
  };

  const validateHeaders = (headers) => {
    const actual = (headers || []).map(normalizeHeaderKey);
    return BULK_REQUIRED_HEADERS.filter((h) => !actual.includes(h));
  };

  const validateBulkRows = (
    rows,
    existing = { phoneSet10: new Set(), emailSet: new Set() },
  ) => {
    const errors = [];
    const users = [];

    const seenPhones = new Map();
    const seenEmails = new Map();

    (rows || []).forEach((row, index) => {
      const rowNo = index + 2;

      const cleanRow = {};
      Object.keys(row || {}).forEach((key) => {
        cleanRow[normalizeHeaderKey(key)] = row[key];
      });

      const roleRaw = String(cleanRow.role ?? "").trim();
      const titleRaw = String(cleanRow.title ?? "").trim();
      const fullname = String(cleanRow.fullname ?? "").trim();
      const dobRaw = cleanRow.dob;
      const phoneRaw = cleanRow.phone;
      const emailRaw = String(cleanRow.email ?? "").trim();

      if (!roleRaw) errors.push(`Row ${rowNo}: role is required`);
      if (!titleRaw) errors.push(`Row ${rowNo}: title is required`);
      if (!fullname) errors.push(`Row ${rowNo}: fullname is required`);
      if (
        dobRaw === null ||
        dobRaw === undefined ||
        String(dobRaw).trim() === ""
      )
        errors.push(`Row ${rowNo}: dob is required`);
      if (
        phoneRaw === null ||
        phoneRaw === undefined ||
        String(phoneRaw).trim() === ""
      )
        errors.push(`Row ${rowNo}: phone is required`);
      if (!emailRaw) errors.push(`Row ${rowNo}: email is required`);

      if (
        !roleRaw ||
        !titleRaw ||
        !fullname ||
        dobRaw === null ||
        dobRaw === undefined ||
        String(dobRaw).trim() === "" ||
        phoneRaw === null ||
        phoneRaw === undefined ||
        String(phoneRaw).trim() === "" ||
        !emailRaw
      ) {
        return;
      }

      const derived = deriveRoleAndSubRole(roleRaw);
      if (!derived) {
        errors.push(
          `Row ${rowNo}: invalid role "${roleRaw}" (allowed: physiotherapist / nurse / nursing assistant)`,
        );
        return;
      }

      const nm = normalizeName(fullname);
      if (!isValidName(nm)) {
        errors.push(
          `Row ${rowNo}: invalid fullname (letters/spaces only, 3-50 chars, min 3 letters)`,
        );
        return;
      }

      const parsedDob = parseDobBulkCell(dobRaw);
      if (!parsedDob) {
        errors.push(
          `Row ${rowNo}: invalid dob "${String(dobRaw)}" (use DD/MM/YYYY or Excel date)`,
        );
        return;
      }
      if (!isDobWithinLast100Years(parsedDob)) {
        errors.push(
          `Row ${rowNo}: invalid dob "${String(dobRaw)}" (must be within last 100 years)`,
        );
        return;
      }

      const phone10 = cleanPhone10Digits(phoneRaw);
      if (!phone10) {
        errors.push(
          `Row ${rowNo}: invalid phone "${String(phoneRaw)}" (10 digits required)`,
        );
        return;
      }

      const emailNorm = normalizeEmail(emailRaw);
      if (!isValidEmail(emailRaw)) {
        errors.push(`Row ${rowNo}: invalid email "${emailRaw}"`);
        return;
      }

      if (seenPhones.has(phone10)) {
        errors.push(
          `Row ${rowNo}: duplicate phone "${phone10}" (already in row ${seenPhones.get(phone10)})`,
        );
        return;
      }
      seenPhones.set(phone10, rowNo);

      if (seenEmails.has(emailNorm)) {
        errors.push(
          `Row ${rowNo}: duplicate email "${emailRaw}" (already in row ${seenEmails.get(emailNorm)})`,
        );
        return;
      }
      seenEmails.set(emailNorm, rowNo);

      if (existing.phoneSet10.has(phone10)) {
        errors.push(`Row ${rowNo}: phone "${phone10}" already exists`);
        return;
      }
      if (existing.emailSet.has(emailNorm)) {
        errors.push(`Row ${rowNo}: email "${emailRaw}" already exists`);
        return;
      }

      users.push({
        role: derived.role,
        subRole: derived.subRole,
        title: capitalizeFirstLetter(titleRaw),
        name: capitalizeFirstLetter(nm),
        dob: parsedDob,
        phone: `91${phone10}`,
        email: emailNorm,
      });
    });

    return { errors, users };
  };

  const draggerProps = {
    name: "file",
    multiple: false,
    accept: ".csv,.xlsx,.xls",
    fileList,
    beforeUpload: (file) => {
      if (!canManage) {
        message.warning("Read-only view. Upload is disabled.");
        return Upload.LIST_IGNORE;
      }

      const ext = String(file.name || "")
        .split(".")
        .pop()
        .toLowerCase();
      const isCsv = ext === "csv";
      const isXlsx = ext === "xlsx" || ext === "xls";

      if (!isCsv && !isXlsx) {
        message.error("You can only upload CSV or Excel (.xlsx/.xls) files!");
        setFileList([]);
        setCsvData([]);
        setBulkErrors([]);
        setBulkValidUsers([]);
        return Upload.LIST_IGNORE;
      }

      setLoading(true);
      const reader = new FileReader();

      const afterParse = (dataRows) => {
        setCsvData(dataRows || []);
        setFileList([file]);

        const { errors, users } = validateBulkRows(
          dataRows || [],
          existingContacts,
        );
        setBulkErrors(errors);
        setBulkValidUsers(users);

        if ((dataRows || []).length === 0) {
          message.error("Uploaded file is empty.");
          return;
        }

        if (errors.length > 0) {
          message.error(
            `File has ${errors.length} validation error(s). Fix and re-upload.`,
          );
        } else {
          message.success(
            `${file.name} parsed & validated successfully (${users.length} rows).`,
          );
        }
      };

      reader.onload = (e) => {
        try {
          if (isCsv) {
            Papa.parse(e.target.result, {
              header: true,
              skipEmptyLines: true,
              complete: (results) => {
                setLoading(false);

                if (results.errors.length > 0) {
                  message.error("Error parsing CSV file.");
                  setCsvData([]);
                  setFileList([]);
                  setBulkErrors([]);
                  setBulkValidUsers([]);
                  return;
                }

                const headers = results.meta.fields || [];
                const missingHeaders = validateHeaders(headers);
                if (missingHeaders.length > 0) {
                  message.error(
                    `Missing headers: ${missingHeaders.join(", ")}`,
                  );
                  setCsvData([]);
                  setBulkErrors([
                    `Missing headers: ${missingHeaders.join(", ")}`,
                  ]);
                  setBulkValidUsers([]);
                  setFileList([file]);
                  return;
                }

                afterParse(results.data || []);
              },
              error: () => {
                setLoading(false);
                message.error("Error parsing CSV file.");
                setCsvData([]);
                setFileList([]);
                setBulkErrors([]);
                setBulkValidUsers([]);
              },
            });
          } else {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, {
              type: "array",
              cellDates: true,
            });
            const sheetName = workbook.SheetNames?.[0];
            const worksheet = workbook.Sheets?.[sheetName];

            const json = XLSX.utils.sheet_to_json(worksheet, {
              defval: "",
              raw: true,
            });
            const headers = json?.[0] ? Object.keys(json[0]) : [];
            const missingHeaders = validateHeaders(headers);

            setLoading(false);

            if (missingHeaders.length > 0) {
              message.error(`Missing headers: ${missingHeaders.join(", ")}`);
              setCsvData([]);
              setBulkErrors([`Missing headers: ${missingHeaders.join(", ")}`]);
              setBulkValidUsers([]);
              setFileList([file]);
              return;
            }

            afterParse(json || []);
          }
        } catch (error) {
          setLoading(false);
          message.error("Error reading file.");
          setCsvData([]);
          setFileList([]);
          setBulkErrors([]);
          setBulkValidUsers([]);
        }
      };

      reader.onerror = () => {
        setLoading(false);
        message.error("Error reading file.");
        setCsvData([]);
        setFileList([]);
        setBulkErrors([]);
        setBulkValidUsers([]);
      };

      if (isCsv) reader.readAsText(file);
      else reader.readAsArrayBuffer(file);

      return false;
    },
    onRemove: () => {
      setFileList([]);
      setCsvData([]);
      setBulkErrors([]);
      setBulkValidUsers([]);
    },
  };

  const handleSubmit = async () => {
    if (!canManage) return;

    if (csvData.length === 0 || fileList.length === 0) {
      showToast("Please upload a file.", "Error");
      return;
    }

    const { errors, users } = validateBulkRows(csvData, existingContacts);
    if (errors.length > 0) {
      setBulkErrors(errors);
      setBulkValidUsers(users);
      message.error(
        `Cannot upload. Fix ${errors.length} error(s) and re-upload.`,
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        type: "bulk",
        onboardedBy: { partnerType: (providerType || "").toLowerCase() },
        data: users.map((u) => ({
          phone: Number(u.phone),
          role: u.role,
          subRole: u.subRole,
          personalInfo: {
            basicInfo: {
              nameInfo: { title: u.title, fullName: u.name },
              otherInfo: { phone: Number(u.phone), email: u.email, dob: u.dob },
            },
          },
        })),
      };

      const response = await SpecialistAdd(payload);

      if (
        response &&
        (response.error === false || response.error === "false")
      ) {
        message.success(response.message || "Specialists added successfully!");
        await getUserList(false);

        setIsOpen(false);
        setCsvData([]);
        setFileList([]);
        setBulkErrors([]);
        setBulkValidUsers([]);
        setEntryMode("single");
        if (onUploadSuccess) onUploadSuccess();
      } else {
        message.error(
          response?.message || "Failed to add specialists. Please try again.",
        );
      }
    } catch (error) {
      message.error(
        "An error occurred during submission. Check console for details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendWelcomeEmailAlert = (specialist) => {
    const hospitalLabel =
      String(partnerName || "").trim() || "Unknown Hospital";
    const specialistName =
      [
        specialist?.personalInfo?.basicInfo?.nameInfo?.title,
        specialist?.personalInfo?.basicInfo?.nameInfo?.fullName,
      ]
        .filter(Boolean)
        .join(" ")
        .trim() || "Unknown Specialist";

    const specialistRole = capitalizeFirstLetter(
      specialist?.subRole || specialist?.role || "Unknown Role",
    );
    const specialistEmail =
      String(
        specialist?.personalInfo?.basicInfo?.otherInfo?.email || "",
      ).trim() || "No email";

    setResendPreviewModal({
      show: true,
      specialistID: String(specialist?._id || ""),
      hospital: hospitalLabel,
      specialist: specialistName,
      role: specialistRole,
      email: specialistEmail,
    });
  };

  const closeResendPreviewModal = () => {
    setResendPreviewModal({
      show: false,
      specialistID: "",
      hospital: "",
      specialist: "",
      role: "",
      email: "",
    });
  };

  const proceedResendPreviewModal = async () => {
    if (!resendPreviewModal.specialistID || !partnerID) {
      showToast("Missing specialist or partner details.", "Error");
      return;
    }

    try {
      setIsResendSending(true);
      const result = await resendWelcomEmailForSpecialist(
        resendPreviewModal.specialistID,
        partnerID,
      );

      if (result?.error === false || result?.error === "false") {
        showToast(
          result?.message || "Welcome email sent successfully.",
          "Success",
        );
      } else {
        showToast(result?.message || "Failed to send welcome email.", "Error");
      }
    } catch (error) {
      showToast("Something went wrong while sending welcome email.", "Error");
    } finally {
      setIsResendSending(false);
      closeResendPreviewModal();
    }
  };

  // table helpers
  const getProfileImage = (original) => {
    const gender =
      original?.personalInfo?.basicInfo?.otherInfo?.gender?.toLowerCase?.();
    const r = original?.role?.toLowerCase?.();
    const subRole = original?.subRole?.toLowerCase?.();
    const isPhysio = r === "physiotherapist" || subRole === "physiotherapist";
    const isGenderless = !gender || !["male", "female"].includes(gender);

    if (isGenderless) {
      return isPhysio
        ? partnerPortalImages.genderLess_physio
        : partnerPortalImages.genderLess_staff;
    }
    if (gender === "male") return CommonImages.menwithoutactive;
    if (gender === "female") return CommonImages.womenwithoutactive;
    return isPhysio
      ? partnerPortalImages.genderLess_physio
      : partnerPortalImages.genderLess_staff;
  };

  const getProfileCompletion = (original) => {
    const score = getProfileCompletionScore(original);
    return { value: `${score}/2`, complete: score === 2 };
  };

  /** search only */
  const searchedList = useMemo(() => {
    let base = Array.isArray(userList) ? [...userList] : [];
    if (searchTerm?.trim()) {
      const term = searchTerm.toLowerCase().trim();
      base = base.filter((item) => {
        const nm =
          item?.personalInfo?.basicInfo?.nameInfo?.fullName?.toLowerCase() ||
          "";
        const ph = String(item?.phone ?? "");
        const em = String(
          item?.personalInfo?.basicInfo?.otherInfo?.email ?? "",
        ).toLowerCase();
        return nm.includes(term) || ph.includes(term) || em.includes(term);
      });
    }
    return base;
  }, [userList, searchTerm]);

  /** counts in filter UI */
  const roleCounts = useMemo(() => {
    const counts = { nurse: 0, physiotherapist: 0, "nursing-assistant": 0 };
    searchedList.forEach((u) => {
      const k = getNormalizedBackendSubRole(u);
      if (counts[k] !== undefined) counts[k] += 1;
    });
    return counts;
  }, [searchedList]);

  const profileCounts = useMemo(() => {
    const counts = { 0: 0, 1: 0, 2: 0 };
    searchedList.forEach((u) => {
      const sc = getProfileCompletionScore(u);
      counts[sc] = (counts[sc] || 0) + 1;
    });
    return counts;
  }, [searchedList]);

  /** apply filters */
  const filteredData = useMemo(() => {
    let data = [...searchedList];

    if (roleFilterKeys.length > 0) {
      data = data.filter((u) =>
        roleFilterKeys.includes(getNormalizedBackendSubRole(u)),
      );
    }

    if (profileFilterKeys.length > 0) {
      data = data.filter((u) =>
        profileFilterKeys.includes(getProfileCompletionScore(u)),
      );
    }

    return data;
  }, [searchedList, roleFilterKeys, profileFilterKeys]);

  useEffect(() => setNoUsersFound(filteredData.length === 0), [filteredData]);

  const columns = useMemo(() => {
    const cols = [
      {
        Header: () => (
          <div className="flex items-center text-[13px] font-poppins text-left">
            Name
          </div>
        ),
        id: "name",
        accessor: (row) =>
          String(
            row?.personalInfo?.basicInfo?.nameInfo?.fullName || "",
          ).toLowerCase(),
        Cell: ({ row: { original } }) => {
          const isPatient = original?.role === "patient";
          const gender = original?.personalInfo?.basicInfo?.otherInfo?.gender;
          const profileSrc =
            original?.profilePicture ||
            (gender === "male"
              ? CommonImages.menwithoutactive
              : gender === "female"
                ? CommonImages.womenwithoutactive
                : CommonImages.commonwithoutactive);

          return (
            <div className="flex items-center text-left text-[12px] min-w-[260px] font-poppins gap-2 ">
              <div className="relative">
                <img
                  src={profileSrc}
                  alt="Profile"
                  className="h-8 w-8 rounded-full object-cover"
                />
                {!isPatient && (
                  <span
                    className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-[1.5px] border-white ${
                      original?.isOnDuty ? "bg-green-500" : "bg-red-500"
                    }`}
                  />
                )}
              </div>
              <span className="">
                {original?.personalInfo?.basicInfo?.nameInfo?.title}
                {". "}
                {capitalizeFirstLetter(
                  original?.personalInfo?.basicInfo?.nameInfo?.fullName || "-",
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins ">Role</div>,
        id: "role",
        accessor: (row) =>
          String(row?.subRole || row?.role || "").toLowerCase(),
        Cell: ({ row: { original } }) => (
          <div className="text-blue-600 font-poppins ">
            {capitalizeFirstLetter(
              original?.role && original?.subRole
                ? original?.subRole
                : original?.role || "-",
            )}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins">Contact</div>,
        id: "contactNumber",
        accessor: (row) => String(row?.phone || ""),
        Cell: ({ row: { original } }) => (
          <div className="text-left text-blue-600 font-poppins">
            <p className="w-40">{formatPhoneNumber(original?.phone)}</p>
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins">DOB</div>,
        id: "dob",
        accessor: (row) => {
          const dobValue = row?.personalInfo?.basicInfo?.otherInfo?.dob;
          if (!dobValue) return 0;
          const ts = new Date(dobValue).getTime();
          return Number.isFinite(ts) ? ts : 0;
        },
        Cell: ({ row: { original } }) => (
          <div className="text-left text-blue-600 font-poppins">
            {moment(original?.personalInfo?.basicInfo?.otherInfo?.dob).format(
              "DD-MM-YYYY",
            )}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins">Profile Completion</div>,
        id: "profileCompletion",
        accessor: (row) => getProfileCompletionScore(row),
        Cell: ({ row: { original } }) => {
          const profileCompletion = getProfileCompletion(original);
          const canResend =
            original?.profileCompletion === "0/2" &&
            !!original?._id &&
            !!partnerID;
          const pc = String(original?.profileCompletion || "").trim();
          const m = pc.match(/^(\d+)\s*\/\s*(\d+)$/); // matches "1/1", "2/2", "5/5", etc.
          const isComplete =
            !!m && Number(m[2]) > 0 && Number(m[1]) === Number(m[2]);

          return (
            <div className="flex items-center justify-center gap-2 min-w-[72px] text-center">
              <div
                className={`font-poppins font-medium text-center ${
                  isComplete ? "text-green-600" : "text-red-600"
                }`}
              >
                {pc || "-"}
              </div>
              {original !== "patient" && (
                <div className="w-[20px] h-[20px] flex items-center justify-center">
                  {canResend && (
                    <button
                      type="button"
                      title="Resend welcome email"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResendWelcomeEmailAlert(original);
                      }}
                      className="p-1 rounded-md transition hover:bg-[#EEF2FF]"
                    >
                      <FiMail size={14} color="#5B60A8" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: () => <div className="font-poppins">Onboared On</div>,
        id: "createdAt",
        accessor: (row) => {
          const ts = new Date(row?.createdAt).getTime();
          return Number.isFinite(ts) ? ts : 0;
        },
        Cell: ({ row: { original } }) => (
          <div className="text-left text-blue-600 font-poppins">
            <p className="w-40">
              {moment(original?.createdAt).format("DD-MM-YYYY")}
            </p>
          </div>
        ),
      },
    ];

    if (show !== "patients") {
      // or: !isPatientView
      cols.push({
        Header: () => <div className="font-poppins">Email ID</div>,
        id: "mail",
        accessor: (row) =>
          String(
            row?.personalInfo?.basicInfo?.otherInfo?.email || "",
          ).toLowerCase(),
        Cell: ({ row: { original } }) => (
          <div className="text-[12px] inline-block max-w-[150px] truncate">
            {original?.personalInfo?.basicInfo?.otherInfo?.email || "-"}
          </div>
        ),
      });
    }

    if (canManage) {
      cols.push({
        Header: () => (
          <div className="font-poppins text-center w-full ml-6">Action</div>
        ),
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row: { original } }) => (
          <div className="flex items-center justify-center">
            <button
              onClick={() => OpenEdit(original)}
              className="p-2 rounded-md hover:bg-[#EEF2FF] transition"
              title="Edit"
            >
              <FiEdit size={14} color="#5B60A8" />
            </button>

            <button
              onClick={() => DeleteUser(original?._id)}
              className="p-2 rounded-md hover:bg-red-50 transition"
              title="Delete"
            >
              <MdDeleteOutline color="red" size={18} />
            </button>
          </div>
        ),
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, partnerID, partnerName]);

  // console.log(filteredData);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    setPageSize,
    setSortBy: setTableSortBy,
    state: { pageIndex, sortBy: currentSortBy },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
        sortBy,
      },
      autoResetPage: false,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination,
  );

  /** persist sort */
  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
    setSortBy(currentSortBy);
  }, [currentSortBy]);

  useEffect(() => {
    if (pageIndex !== paginationPage) setPaginationPage(pageIndex);
  }, [pageIndex, paginationPage]);

  /** onboarded sort => radio (Latest/Oldest) */
  useEffect(() => {
    if (onboardSort === "latest")
      setTableSortBy([{ id: "createdAt", desc: true }]);
    if (onboardSort === "oldest")
      setTableSortBy([{ id: "createdAt", desc: false }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardSort]);

  /** when filters/search change -> page 1 */
  useEffect(() => {
    setPaginationPage(0);
    gotoPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilterKeys, profileFilterKeys, searchTerm]);

  /** filter handlers */
  const toggleRoleFilter = (key) => {
    setRoleFilterKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const toggleProfileFilter = (key) => {
    setProfileFilterKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const resetFilters = () => {
    setRoleFilterKeys([]);
    setProfileFilterKeys([]);
    setOnboardSort("latest"); // default Latest
  };

  /** page count handler */
  const handlePageCountChange = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n)) return;
    localStorage.setItem("pageCount", String(n));
    setSelectedPageCount(n);
    setPageSize(n);
    setPaginationPage(0);
    gotoPage(0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <Loader loading={loading} />
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#F3F6F5]">
        {selectedTab !== "Assigned Staffs" ? (
          <div className="overflow-x-auto bg-white rounded-2xl w-auto shadow-lg p-2 h-[88vh] m-2 flex flex-col gap-3">
            <div className="flex justify-between items-center mt-[-25px]">
              <div className="flex flex-row items-center mt-6 ml-2">
                <div className="rounded-full bg-[#cadefa] p-2">
                  <RiUserAddFill color="#5B60A8" size={18} />
                </div>
                <p className="text-[14px] font-bold ml-2 font-poppins">
                  <p className="text-[14px] font-bold ml-2 font-poppins">
                    {show !== "patients"
                      ? `Staff list (${filteredData.length})`
                      : `Patient list (${filteredData.length})`}
                  </p>
                </p>
              </div>

              {/* Right actions */}
              {show !== "patients" && (
                <div
                  className="flex items-center gap-2 mt-8 mr-2 relative mb-2"
                  ref={filterRef}
                >
                  {/* page count dropdown */}
                  <div className="flex items-center bg-white border border-blue-600 rounded-md px-2 h-[32px]">
                    <select
                      value={selectedPageCount}
                      onChange={(e) => handlePageCountChange(e.target.value)}
                      className="outline-none font-poppins text-[13px] text-blue-600  bg-transparent"
                    >
                      {[25, 50, 75, 100].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ✅ Filters bar (icon + arrow up/down) */}
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen((s) => !s)}
                    className="flex items-center justify-between gap-2 border border-blue-600 rounded-md px-3 h-[32px] font-poppins text-[13px] text-[#2F3A8F] bg-white min-w-[170px]"
                  >
                    <span className="flex items-center gap-2">
                      <MdOutlineFilterList className="h-5 w-5" />
                      Filters
                    </span>
                    {isFilterOpen ? (
                      <MdKeyboardArrowUp className="h-5 w-5" />
                    ) : (
                      <MdKeyboardArrowDown className="h-5 w-5" />
                    )}
                  </button>

                  {/* ✅ Filters dropdown */}
                  {isFilterOpen && (
                    <div className="absolute right-0 top-[44px] w-[250px] bg-white border border-[#D7DBFF] rounded-xl shadow-lg p-2 z-40">
                      <div className="flex items-center justify-between">
                        <p className="font-poppins text-[13px] font-semibold text-[#1F2937]">
                          Filters
                        </p>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={resetFilters}
                            className="font-poppins text-[12px] text-[#4951EC] underline"
                          >
                            Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsFilterOpen(false)}
                            className="p-1 rounded hover:bg-[#EEF2FF]"
                          >
                            <MdClose size={18} color="#4951EC" />
                          </button>
                        </div>
                      </div>

                      {/* ✅ Onboarded On (Radio) */}
                      <div className="mt-3">
                        <p className="font-poppins text-[12px] font-semibold text-[#374151] mb-2">
                          Onboared On
                        </p>

                        <div className="flex flex-col gap-2">
                          <label className="flex items-center gap-2 font-poppins text-[12px] text-[#374151]">
                            <input
                              type="radio"
                              name="onboardedOn"
                              checked={onboardSort === "latest"}
                              onChange={() => setOnboardSort("latest")}
                            />
                            Latest
                          </label>

                          <label className="flex items-center gap-2 font-poppins text-[12px] text-[#374151]">
                            <input
                              type="radio"
                              name="onboardedOn"
                              checked={onboardSort === "oldest"}
                              onChange={() => setOnboardSort("oldest")}
                            />
                            Oldest
                          </label>
                        </div>
                      </div>

                      {/* Role */}
                      <div className="mt-4">
                        <p className="font-poppins text-[12px] font-semibold text-[#374151] mb-2">
                          Role
                        </p>
                        <div className="flex flex-col gap-2">
                          {ROLE_FILTERS.map((r) => (
                            <label
                              key={r.key}
                              className="flex items-center gap-2 font-poppins text-[12px] text-[#374151]"
                            >
                              <input
                                type="checkbox"
                                checked={roleFilterKeys.includes(r.key)}
                                onChange={() => toggleRoleFilter(r.key)}
                              />
                              <span className="flex-1">{r.label}</span>
                              <span className="text-[#6B7280]">
                                ({roleCounts[r.key] || 0})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Profile Completion */}
                      <div className="mt-4">
                        <p className="font-poppins text-[12px] font-semibold text-[#374151] mb-2">
                          Profile Completion
                        </p>
                        <div className="flex flex-col gap-2">
                          {PROFILE_FILTERS.map((p) => (
                            <label
                              key={p.key}
                              className="flex items-center gap-2 font-poppins text-[12px] text-[#374151]"
                            >
                              <input
                                type="checkbox"
                                checked={profileFilterKeys.includes(p.key)}
                                onChange={() => toggleProfileFilter(p.key)}
                              />
                              <span className="flex-1">{p.label}</span>
                              <span className="text-[#6B7280]">
                                ({profileCounts[p.key] || 0})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Add button */}
                  {canManage && (
                    <div
                      className="flex flex-row items-center bg-[#4951EC] gap-1 p-2 rounded-md cursor-pointer"
                      onClick={AddUser}
                    >
                      <IoIosAddCircleOutline color="white" size={18} />
                      <p className="text-[12px] text-[white] font-poppins">
                        Add Team Member
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="overflow-x-auto max-h-[75vh] mt-[-10px]">
              <div className="min-w-full w-[100%]">
                <ReusableTable
                  columns={columns}
                  getTableProps={getTableProps}
                  getTableBodyProps={getTableBodyProps}
                  headerGroups={headerGroups}
                  tablePage={tablePage}
                  prepareRow={prepareRow}
                  filterIcon={CommonImages.filter}
                  searchTerm={searchTerm}
                  errorMsg={"No Care Team Members Found"}
                />
              </div>
            </div>

            <div className="mb-2">
              <Page
                paginationPage={paginationPage}
                nextPage={nextPage}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                screen="hospital"
              />
            </div>
          </div>
        ) : (
          <div />
        )}
      </div>

      {isOpen && canManage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="flex flex-col bg-white shadow-lg w-[95%] border border-[#9EA3FF] max-w-[650px] p-4 h-auto max-h-[80vh] rounded-[10px] overflow-y-auto">
            <div className="flex justify-between bg-white p-4">
              <div className="flex items-center gap-2">
                <RiUserAddFill color="#4951EC" size={20} />
                <span className="font-poppins text-sm text-[#4951EC]">
                  Care Team Onboarding
                </span>
              </div>
              <div
                onClick={() => {
                  setIsOpen(false);
                  setEntryMode("single");
                  setCsvData([]);
                  setFileList([]);
                  setBulkErrors([]);
                  setBulkValidUsers([]);
                }}
                className="cursor-pointer"
              >
                <MdClose color="#4951EC" size={20} />
              </div>
            </div>

            <div className="flex flex-col items-center justify-center mx-auto gap-4 p-4 w-full">
              {!serviceInfoPage && (
                <ToggleTabs
                  options={[
                    { label: "Single Entry", value: "single" },
                    ...(edit !== true
                      ? [{ label: "Bulk Entry", value: "bulk" }]
                      : []),
                  ]}
                  active={entryMode}
                  setActive={(mode) => {
                    setEntryMode(mode);
                    if (mode === "single") {
                      setCsvData([]);
                      setFileList([]);
                      setBulkErrors([]);
                      setBulkValidUsers([]);
                    }
                  }}
                />
              )}

              {entryMode === "single" ? (
                <div className="flex flex-col gap-4 max-w-[1100px] mx-auto w-full">
                  <div className="flex gap-4 mt-4 mb-4">
                    <div className="flex-1">
                      <div className="flex flex-row gap-10">
                        <label className="text-[14px] font-poppins text-[#606060] block mb-2">
                          Title
                        </label>
                        <label className="text-[14px] font-poppins text-[#606060] block mb-2">
                          Name as in Aadhaar{" "}
                          <span className="text-red-500">*</span>
                        </label>
                      </div>

                      <div className="flex items-center bg-[#F6F8FC] rounded-[8px]">
                        <div className="relative w-[65px]">
                          <select
                            className="appearance-none w-full bg-transparent px-2 h-[38px] rounded-l-[8px] text-[14px] text-[#606060] font-poppins pr-6 outline-none"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                          >
                            <option value="" disabled hidden>
                              Title
                            </option>
                            {caseList
                              .filter((item) => item.label !== "Master.")
                              .map((item, index) => (
                                <option
                                  key={`${item.value}-${index}`}
                                  value={item.value}
                                >
                                  {item.label}
                                </option>
                              ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <svg
                              className="w-4 h-4 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </div>
                        </div>

                        <div className="h-[20px] w-[1.5px] bg-[#787998A8]"></div>

                        <input
                          type="text"
                          className="flex-1 bg-transparent px-2 h-[38px] rounded-r-[8px] text-[14px] font-poppins text-[#606060] outline-none"
                          value={name}
                          onChange={(e) =>
                            setName(sanitizeNameInput(e.target.value))
                          }
                          placeholder="Enter full name"
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      <label className="text-[14px] text-[#606060] font-poppins block mb-2">
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={dob}
                        onChange={handleDobChange}
                        min={minDate}
                        max={maxDate}
                        className="w-full bg-[#F6F8FC] px-2 h-[38px] rounded-[8px] text-[14px] font-poppins text-[#606060]"
                      />
                    </div>

                    <div className="flex-1">
                      <label className="text-[14px] mb-2 block text-[#606060] font-poppins">
                        Select Role <span className="text-red-500">*</span>
                      </label>
                      <div className="relative w-[160px]">
                        <select
                          className="w-full text-[14px] font-poppins text-[#606060] bg-[#F6F8FC] px-2 h-[38px] rounded-[8px] appearance-none pr-6"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                        >
                          <option value="" disabled hidden></option>
                          <option value="Nurse">Nurse</option>
                          <option value="Physiotherapist">
                            Physiotherapist
                          </option>
                          <option value="Nursing-Assistant">
                            Nursing Assistant
                          </option>
                        </select>

                        <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
                          <svg
                            className="w-4 h-4 text-blue-600"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4 mb-4">
                    <div className="flex-1">
                      <label className="text-[14px] text-[#606060] font-poppins block mb-2">
                        Contact Number <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center bg-[#F6F8FC] rounded-[8px]">
                        <div className="relative w-[70px]">
                          <select
                            className="appearance-none w-full bg-transparent px-2 h-[38px] rounded-l-[8px] text-[14px] font-poppins text-[#606060] pr-6 outline-none"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                          >
                            {caseCode.map((item, index) => (
                              <option key={index} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <svg
                              className="w-4 h-4 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                            >
                              <path d="M6 9l6 6 6-6" />
                            </svg>
                          </div>
                        </div>

                        <div className="h-[20px] w-[1.5px] bg-[#787998A8]"></div>

                        <input
                          type="tel"
                          maxLength={10}
                          className="flex-1 bg-transparent px-2 h-[38px] rounded-r-[8px] text-[14px] font-poppins text-[#606060] outline-none"
                          value={phone}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) setPhone(value);
                          }}
                          placeholder="10 digits"
                        />
                      </div>
                    </div>

                    <div className="flex-1">
                      <label className="text-[14px] text-[#606060] font-poppins block mb-2">
                        Email ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className="w-full px-2 h-[38px] rounded-[8px] text-[14px] font-poppins text-[#606060] bg-[#F6F8FC]"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex justify-center mt-4 pb-6">
                    {!edit ? (
                      <button
                        className="bg-custom-gradient px-6 rounded-[10px] py-2 text-white font-poppins"
                        onClick={SaveUser}
                        disabled={loading}
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        className="bg-custom-gradient px-6 rounded-[10px] py-2 text-white font-poppins"
                        onClick={SaveEdit}
                        disabled={loading}
                      >
                        Update
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <Card
                  className="w-full"
                  bodyStyle={{ padding: "6px" }}
                  style={{ border: "none", boxShadow: "none" }}
                >
                  <Space direction="vertical" size="small" className="w-full">
                    <div className="text-center">
                      <p className="font-poppins font-medium text-[16px] mb-6">
                        Import Bulk HCP
                      </p>
                      <p className="font-poppins text-[#8177A8] text-[13px] mt-2 mb-4">
                        Download Sample Template and upload it here.
                      </p>

                      <div
                        className="bg-custom-gradient w-[240px] rounded-md flex items-center justify-center mx-auto py-2 mt-3 mb-4 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={downloadSampleExcel}
                      >
                        <FiDownload
                          color="#FFFFFF"
                          size={16}
                          className="mr-2"
                        />
                        <p className="text-center text-[#FFFFFF] text-[13px] font-poppins">
                          Download Sample Template
                        </p>
                      </div>
                    </div>

                    <Dragger
                      {...draggerProps}
                      style={{ width: "100%", padding: "15px 0" }}
                      disabled={loading}
                    >
                      <img
                        src={CommonImages.csvUpload}
                        className="w-10 h-10 mx-auto mb-2"
                        alt="csv"
                      />
                      <p className="ant-upload-text font-poppins text-[10px]">
                        Choose a file or drag it here (.csv / .xlsx)
                      </p>
                    </Dragger>

                    {fileList.length > 0 && (
                      <div className="mt-2 w-full">
                        {bulkErrors.length > 0 ? (
                          <div className="bg-red-50 border border-red-200 rounded-md p-3">
                            <p className="text-[12px] font-poppins text-red-700">
                              Found {bulkErrors.length} error(s). Fix and
                              re-upload the file.
                            </p>
                            <ul className="list-disc pl-5 mt-2">
                              {bulkErrors.slice(0, 8).map((e, idx) => (
                                <li
                                  key={idx}
                                  className="text-[12px] font-poppins text-red-700"
                                >
                                  {e}
                                </li>
                              ))}
                            </ul>
                            {bulkErrors.length > 8 && (
                              <p className="text-[11px] font-poppins text-red-700 mt-2">
                                ...and {bulkErrors.length - 8} more
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="bg-green-50 border border-green-200 rounded-md p-3">
                            <p className="text-[12px] font-poppins text-green-700">
                              ✓ File validated successfully.{" "}
                              {bulkValidUsers.length} row(s) ready to upload.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex justify-center mt-4">
                      <Button
                        onClick={handleSubmit}
                        type="primary"
                        disabled={
                          loading ||
                          csvData.length === 0 ||
                          fileList.length === 0 ||
                          bulkErrors.length > 0
                        }
                        loading={loading}
                        className="font-poppins px-6 h-[36px]"
                        style={{
                          background:
                            csvData.length > 0 && bulkErrors.length === 0
                              ? "linear-gradient(90deg, #3671E9 0%, #5B60A8 100%)"
                              : undefined,
                        }}
                      >
                        Upload & Save
                      </Button>
                    </div>
                  </Space>
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {resendPreviewModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-[10px] shadow-lg w-full max-w-[520px] p-5">
            <h3 className="font-poppins text-[16px] font-semibold text-[#1F2937] mb-3">
              Resend Welcome Email
            </h3>
            <p className="font-poppins text-[13px] text-[#4B5563] mb-3">
              Please verify the target details before proceeding.
            </p>

            <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-[8px] p-3 mb-4">
              <p className="font-poppins text-[13px] text-[#111827] mb-1">
                <span className="font-semibold">Hospital:</span>{" "}
                {resendPreviewModal.hospital}
              </p>
              <p className="font-poppins text-[13px] text-[#111827] mb-1">
                <span className="font-semibold">Specialist:</span>{" "}
                {resendPreviewModal.specialist} ({resendPreviewModal.role})
              </p>
              <p className="font-poppins text-[13px] text-[#111827]">
                <span className="font-semibold">Email:</span>{" "}
                {resendPreviewModal.email}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-[8px] border border-[#D1D5DB] text-[#374151] font-poppins text-[13px] hover:bg-[#F3F4F6]"
                disabled={isResendSending}
                onClick={closeResendPreviewModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-[8px] text-white font-poppins text-[13px] ${
                  isResendSending
                    ? "bg-[#9CA3AF] cursor-not-allowed"
                    : "bg-[#4951EC] hover:opacity-90"
                }`}
                disabled={isResendSending}
                onClick={proceedResendPreviewModal}
              >
                {isResendSending ? "Sending..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MCT;
